'use client';

import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import type { FieldValues } from 'react-hook-form';

import {
	buildFieldPath as buildPath, isNonEmptyString, resetField,
} from '@/modules/shared/presentation/utils';
import type { BuildFieldPath } from '@/modules/shared/presentation/utils';

import type { SelectInputProps } from '@packages/hook-form-wrapper/src/components/inputs';

type FieldKey = 'industryId' | 'subIndustryId';

type IndustryFormFields = {
	industryId?:    string
	subIndustryId?: string
};

type IndustryFieldValues = FieldValues & (
	| IndustryFormFields
	| Record<string, IndustryFormFields>
);

function buildFieldPath<
	TFieldValues extends IndustryFieldValues,
	TBaseName extends string | undefined,
	TFieldKey extends FieldKey,
>(
	baseName: TBaseName | undefined,
	fieldKey: TFieldKey,
): BuildFieldPath<TFieldValues, TBaseName, TFieldKey> {
	return buildPath(baseName, fieldKey) as BuildFieldPath<TFieldValues, TBaseName, TFieldKey>;
}

export function useIndustryFormWatch<
	TFieldValues extends IndustryFieldValues,
	TBaseName extends string | undefined = undefined,
>(baseName?: TBaseName) {
	const { control, setValue } = useFormContext<TFieldValues>();

	const industryPath = buildFieldPath<TFieldValues, TBaseName, 'industryId'>(baseName, 'industryId');
	const subIndustryPath = buildFieldPath<TFieldValues, TBaseName, 'subIndustryId'>(baseName, 'subIndustryId');

	const industryIdValue = useWatch({
		control,
		name: industryPath,
	});

	const industryId = isNonEmptyString(industryIdValue) ? industryIdValue : undefined;

	useEffect(() => {
		if (industryId !== undefined) {
			resetField(setValue, subIndustryPath);
		}
	}, [ industryId, setValue, subIndustryPath ]);

	const industry: Pick<SelectInputProps<TFieldValues>, 'name'> = { name: industryPath };

	const subIndustry: Pick<SelectInputProps<TFieldValues>, 'disabled' | 'name'> = {
		disabled: !industryId,
		name:     subIndustryPath,
	};

	return {
		industryId,

		industry,
		subIndustry,
	};
}
